import MuiButton from '@mui/material/Button';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { FC, useCallback, useState } from 'react';
import ButtonStyles from './Button.styles';
import { ButtonProps, ButtonState, ButtonType, StylesByType } from './Button.types';
import IconButtonStyles from './IconButton.styles';
import { YodaBorderRadius } from 'yoda-ui/yodaTheme';

const useButtonStyles = (type: ButtonType, spec: StylesByType, buttonWidth?: number): SxProps<Theme> => ({
  padding: '0.75rem 1.5rem',
  borderRadius: `${YodaBorderRadius.xxxLarge}`,
  boxSizing: 'border-box',
  textTransform: 'none',
  minWidth: 'min-content',
  maxHeight: '48px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: 'fit-content',
  ...buttonWidth && { width: `${buttonWidth}px` },
  ...spec[ButtonState.normal][type],
  '&:hover': {
    ...spec[ButtonState.hover][type],
    cursor: 'pointer',
  },
  '&.Mui-disabled': {
    ...spec[ButtonState.disabled][type],
  },
  '&.Mui-focusVisible': {
    ...spec[ButtonState.focus][type],
  },
});

const Button: FC<ButtonProps> = (props) => {
  const {
    buttonType = ButtonType.neutral,
    ...rest
  } = props;
  const { startIcon } = props;
  const buttonSpec = startIcon ? IconButtonStyles : ButtonStyles;
  const [buttonWidth, setButtonWidth] = useState(0);

  const measuredRef = useCallback((node: HTMLButtonElement | null) => {
    if (node !== null) {
      setButtonWidth(node.getBoundingClientRect().width);
    }
  }, []);

  const buttonStyle = useButtonStyles(buttonType, buttonSpec, buttonWidth);

  return (
    <MuiButton
      { ...rest }
      disableElevation
      disableRipple
      ref={ measuredRef }
      sx={ { ...buttonStyle as object, '& .MuiButton-startIcon': { marginRight: 0 } } }
    />
  );
};

export default Button;
